import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { TemplateProvider, useTemplate } from "@/context/TemplateContext";
import { CartProvider } from "@/context/CartContext";
import EcomHeader from "@/pages/template-ecommerce/components/Header";
import EcomFooter from "@/pages/template-ecommerce/components/Footer";
import EcomHome from "@/pages/template-ecommerce/Home";
import EcomShop from "@/pages/template-ecommerce/Shop";
import "@/pages/template-ecommerce/ecommerce-theme.css";

import ServiceHeader from "@/template-service/components/Header";
import ServiceFooter from "@/template-service/components/Footer";
import ServiceHome from "@/template-service/pages/Home";
import ServiceServices from "@/template-service/pages/Services";
import ServiceAbout from "@/template-service/pages/About";
import ServiceConsultations from "@/template-service/pages/Consultations";
import ServiceReviews from "@/template-service/pages/Reviews";
import ServiceFaq from "@/template-service/pages/FAQ";
import ServiceContact from "@/template-service/pages/Contact";
import { ServiceBasePathProvider } from "@/template-service/lib/route";
import { setServiceData } from "@/template-service/lib/_data";

import RestaurantLanding from "@/template-restaurant/pages/Landing";
import { setRestaurantData } from "@/template-restaurant/lib/_data";
import "@/template-restaurant/restaurant.css";

import { findEditableAnchor, resolveAnchorForPage } from "@/components/dashboard/inline-editor/editable-anchors";
import ContextualToolbar from "@/components/dashboard/inline-editor/ContextualToolbar";
import { ECOM_STATIC_PAGES } from "@/pages/template-ecommerce/StaticPages";

const DEVICE_WIDTHS = {
  desktop: "100%",
  tablet: "820px",
  mobile: "420px",
};

/**
 * Mounts the appropriate SRC template shell INSIDE the editor (no iframe).
 *
 * Rendering strategy per template:
 *  • service    → sync `_data` module with the effective config, then render
 *                 <ServiceHeader/><ServiceHome/><ServiceFooter/> inside a
 *                 <ServiceBasePathProvider basePath="/site/<slug>">
 *  • restaurant → sync `_data` module with effective restaurant_config, then
 *                 render <RestaurantLanding slug={slug} /> in a themed wrapper
 *  • ecommerce  → mount <TemplateProvider overrides={ecommerce_config}> +
 *                 <CartProvider> + chrome around <EcomHome/>.
 *
 * Iter 76 — explicit Edit mode:
 *  • When `isEditMode` is false the canvas is a clean, full-width preview:
 *    no hover outlines, no click-to-select, no editing chrome. Link/form
 *    navigation is still suppressed so the outer app never navigates away.
 *  • When `isEditMode` is true, clicking an EDITABLE_ANCHORS element selects
 *    it: green outline + a thin contextual toolbar (Content | Style |
 *    Advanced) rendered by <ContextualToolbar/> INSIDE the frame so it moves
 *    with the element. Clicking empty canvas space deselects.
 *
 * All rects are computed relative to the canvas FRAME (which is the
 * containing block thanks to its `transform`), so outlines stay pinned
 * while the scroller scrolls without extra listeners.
 */
export default function EditableCanvas({
  payload, template, slug, pageSlug, deviceMode, selection, setSelection,
  isEditMode, onDeselect, editorProps,
}) {
  const canvasRef = useRef(null);
  const scrollerRef = useRef(null);
  const outerRef = useRef(null);
  const [outline, setOutline] = useState(null); // { top, left, width, height, label, frameWidth, fallback? }
  const [hoverOutline, setHoverOutline] = useState(null);

  // ─── Global click handler: DOM → editable anchor → selection ──────────
  // Attached to the OUTER canvas container (not the frame) so clicks in the
  // gutter around the site frame also deselect.
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;

    const onClick = (e) => {
      // Clicks inside the contextual toolbar/popover are editor UI — let
      // them through untouched (inputs, buttons, contentEditable).
      if (e.target.closest?.("[data-inline-editor-ui]")) return;

      if (isEditMode) {
        const match = findEditableAnchor(e.target, e.clientX, e.clientY);
        if (match) {
          e.preventDefault();
          e.stopPropagation();
          const resolved = resolveAnchorForPage(match.anchor, template, pageSlug);
          setSelection({
            anchor: resolved,
            elementTestId: match.element?.getAttribute?.("data-testid") || match.anchor.testid,
          });
          return;
        }
      }
      // Non-editable click. Suppress navigation on links/buttons that would
      // otherwise navigate the outer app away from /dashboard. Form submits
      // are also blocked so nothing accidentally hits the backend.
      const link = e.target.closest("a[href]");
      const btn = e.target.closest("button[type='submit']");
      if (link || btn) {
        e.preventDefault();
        e.stopPropagation();
      }
      // Click-away on empty canvas space deselects.
      if (isEditMode) setSelection(null);
    };

    const onSubmit = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const onMouseOver = (e) => {
      if (!isEditMode) { setHoverOutline(null); return; }
      if (e.target.closest?.("[data-inline-editor-ui]")) { setHoverOutline(null); return; }
      const match = findEditableAnchor(e.target, e.clientX, e.clientY);
      if (!match) { setHoverOutline(null); return; }
      const rect = getRectRelativeTo(match.element, canvasRef.current);
      setHoverOutline({ ...rect, label: match.anchor.label });
    };
    const onMouseOut = () => setHoverOutline(null);

    el.addEventListener("click", onClick, true);
    el.addEventListener("submit", onSubmit, true);
    el.addEventListener("mouseover", onMouseOver, true);
    el.addEventListener("mouseleave", onMouseOut, true);
    return () => {
      el.removeEventListener("click", onClick, true);
      el.removeEventListener("submit", onSubmit, true);
      el.removeEventListener("mouseover", onMouseOver, true);
      el.removeEventListener("mouseleave", onMouseOut, true);
    };
  }, [setSelection, isEditMode, template, pageSlug]);

  // Clear hover artifacts when leaving Edit mode.
  useEffect(() => {
    if (!isEditMode) setHoverOutline(null);
  }, [isEditMode]);

  // ─── Keep the selection outline pinned to the selected element ────────
  useLayoutEffect(() => {
    if (!selection?.anchor || !canvasRef.current) { setOutline(null); return; }
    const frame = canvasRef.current;
    const update = () => {
      const tid = selection.elementTestId || selection.anchor?.testid;
      const el = tid ? frame.querySelector(`[data-testid="${tid}"]`) : null;
      if (!el) {
        // Element not in the DOM (e.g. synthetic selection from the left
        // panel for a list that doesn't render on this page). Pin the
        // toolbar near the top of the current viewport instead.
        const scrollTop = scrollerRef.current?.scrollTop ?? 0;
        setOutline({
          top: Math.max(56, scrollTop - 56 + 72),
          left: 24,
          width: 0,
          height: 0,
          label: selection.anchor.label,
          frameWidth: frame.offsetWidth,
          fallback: true,
        });
        return;
      }
      setOutline({
        ...getRectRelativeTo(el, frame),
        label: selection.anchor.label,
        frameWidth: frame.offsetWidth,
      });
    };
    update();
    // React to layout changes (typography edits, image loads, device switch)
    const ro = new ResizeObserver(update);
    ro.observe(frame);
    const tid = selection.elementTestId || selection.anchor?.testid;
    const el = tid ? frame.querySelector(`[data-testid="${tid}"]`) : null;
    if (el) ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [selection, payload, deviceMode]);

  const width = DEVICE_WIDTHS[deviceMode] || DEVICE_WIDTHS.desktop;

  return (
    <div ref={outerRef} className="flex-1 min-w-0 bg-[#eeeeea] overflow-hidden relative" data-testid="inline-editor-canvas">
      {/* Edit-mode hint — only visible while editing with nothing selected */}
      {isEditMode && !selection && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
          <div className="px-3 py-1.5 rounded-full bg-emerald-500/95 text-white text-xs font-black shadow-lg" data-testid="inline-editor-click-hint">
            Click any element to edit it
          </div>
        </div>
      )}

      <div ref={scrollerRef} className="w-full h-full overflow-auto flex justify-center pt-14 pb-8">
        <div
          ref={canvasRef}
          className="bg-white shadow-xl mx-auto min-h-full relative"
          style={{
            width,
            transition: "width 200ms",
            // Force this element to be the containing block for any
            // `position: fixed` descendants (SRC templates put their
            // Header at `fixed top-0 z-50`). Without this the SRC header
            // escapes the editor canvas and covers the editor's own
            // TopBar. `transform` and `filter` both create a new
            // containing block per CSS spec.
            transform: "translateZ(0)",
          }}
          data-testid="inline-editor-canvas-frame"
        >
          {template === "service" && (
            <ServiceRenderer payload={payload} slug={slug} pageSlug={pageSlug} />
          )}
          {template === "restaurant" && (
            <RestaurantRenderer payload={payload} slug={slug} />
          )}
          {template === "ecommerce" && (
            <EcommerceRenderer payload={payload} slug={slug} pageSlug={pageSlug} />
          )}
          {!["service", "restaurant", "ecommerce"].includes(template) && (
            <div className="p-12 text-center text-gray-500">
              This template is not yet supported in the inline visual editor.
            </div>
          )}

          {/* Hover outline (Edit mode only) */}
          {isEditMode && hoverOutline && !outline && (
            <div
              className="absolute pointer-events-none ring-2 ring-emerald-400 ring-offset-2 ring-offset-transparent rounded-sm z-20"
              style={{ top: hoverOutline.top, left: hoverOutline.left, width: hoverOutline.width, height: hoverOutline.height }}
              data-testid="inline-editor-hover-outline"
            />
          )}

          {/* Selection outline */}
          {outline && !outline.fallback && (
            <div
              className="absolute pointer-events-none ring-2 ring-emerald-500 rounded-sm z-20"
              style={{ top: outline.top, left: outline.left, width: outline.width, height: outline.height }}
              data-testid="inline-editor-selection-outline"
            />
          )}

          {/* Contextual toolbar + anchored popover — attached to the element */}
          {selection && outline && (
            <ContextualToolbar
              selection={selection}
              outline={outline}
              editorProps={editorProps}
              onDeselect={onDeselect}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Utilities                                                                  */
/* -------------------------------------------------------------------------- */

// Rect of `el` relative to the canvas FRAME (`container`). Because the frame
// scrolls together with its content, the difference of the two client rects
// is scroll-invariant — no scroll listener needed.
function getRectRelativeTo(el, container) {
  const r = el.getBoundingClientRect();
  const c = container.getBoundingClientRect();
  return {
    top: r.top - c.top,
    left: r.left - c.left,
    width: r.width,
    height: r.height,
  };
}

/* -------------------------------------------------------------------------- */
/* SRC template renderers                                                     */
/* -------------------------------------------------------------------------- */

/** Same slug → component map used by `PublicServiceShell.jsx`. Unknown
 * slugs (e.g. a user-added custom page) fall back to Home so the canvas
 * never renders a blank page. */
const SERVICE_PAGE_MAP = {
  "": ServiceHome,
  home: ServiceHome,
  services: ServiceServices,
  about: ServiceAbout,
  consultations: ServiceConsultations,
  reviews: ServiceReviews,
  faq: ServiceFaq,
  contact: ServiceContact,
};

function ServiceRenderer({ payload, slug, pageSlug }) {
  // Sync the service template's `_data` module with the current effective
  // config so its Header/Footer/Home read the edited values immediately.
  useMemo(() => {
    if (payload?.service_config) setServiceData(payload.service_config);
  }, [payload?.service_config]);

  const PageComponent = SERVICE_PAGE_MAP[pageSlug || "home"] || ServiceHome;

  // NOTE: no MemoryRouter — the app's outer BrowserRouter is already in the
  // React tree and React Router forbids nesting Routers. The editor canvas
  // suppresses navigation by intercepting all clicks (see canvas onClick).
  return (
    <ServiceBasePathProvider basePath={`/site/${slug}`}>
      <div
        className="min-h-screen bg-[#f9f6f0] text-[#2c2a29] font-body"
        data-testid="editor-service-shell"
        data-page-slug={pageSlug || "home"}
      >
        <ServiceHeader />
        <main>
          <PageComponent />
        </main>
        <ServiceFooter />
      </div>
    </ServiceBasePathProvider>
  );
}

function RestaurantRenderer({ payload, slug }) {
  useMemo(() => {
    if (payload?.restaurant_config) setRestaurantData(payload.restaurant_config);
  }, [payload?.restaurant_config]);
  return (
    <div className="restaurant-template" data-testid="editor-restaurant-shell">
      <RestaurantLanding slug={slug} />
    </div>
  );
}

function EcommerceRenderer({ payload, slug, pageSlug }) {
  const basePath = `/site/${slug}`;
  return (
    <TemplateProvider
      industryKey={null}
      overrides={payload?.ecommerce_config || {}}
      basePath={basePath}
      slug={slug}
      checkoutEnabled={!!payload?.checkout_enabled}
      websiteContent={payload?.website_content || {}}
      summary={payload?.summary || {}}
    >
      <EcomChrome slug={slug} basePath={basePath} pageSlug={pageSlug} />
    </TemplateProvider>
  );
}

/** Ecommerce slug → template Page component. Standard sub-pages (about /
 * contact / faq / best-sellers / policies) render the same SRC components
 * the public site uses (Iter 77). Any unknown slug (checkout, order-success,
 * product-detail, or a user-added custom page) falls back to Home so the
 * canvas always shows something. */
const ECOM_PAGE_MAP = {
  "": EcomHome,
  home: EcomHome,
  shop: EcomShop,
  ...ECOM_STATIC_PAGES,
};

function EcomChrome({ slug, basePath, pageSlug }) {
  const PageComponent = ECOM_PAGE_MAP[pageSlug || "home"] || EcomHome;
  const { config } = useTemplate();
  // Reuse the exact same chrome the public shell uses, so click-to-select
  // via testids works identically to what visitors see.
  return (
    <CartProvider pricing={{}}>
      <div
        className="strvkt-theme min-h-screen flex flex-col"
        data-testid="editor-ecommerce-shell"
        data-page-slug={pageSlug || "home"}
      >
        <EcomHeader
          basePath={basePath}
          transparent={true}
          announcementMessages={config?.announcementMessages}
        />
        <main className="flex-1">
          <PageComponent />
        </main>
        <EcomFooter basePath={basePath} />
      </div>
    </CartProvider>
  );
}
